import { ModalBody, ModalFooter, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, TextField } from '@main/complykit';
import { useCreateProgramMutation } from '@main/graphql/mutations/CreateProgram.generated';
import { useInsertFrameworkMutation } from '@main/graphql/mutations/InsertFramework.generated';
import { useGetAvailableFrameworksForOrganizationQuery } from '@main/graphql/queries/GetAvailableFrameworksForOrganization.generated';
import { useGetProgramsQuery } from '@main/graphql/queries/GetPrograms.generated';
import { FrameworkType } from '@main/graphql/types.generated';
import { useStableCallback } from '@main/shared/browser';
import { errorToast, successToast, useModal } from '@main/ui';
import { FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

type CreateCustomProgramModalProps = {
  orgId: string;
};

const createCustomProgramSchema = z.object({
  programName: z.string().min(1, 'Enter a name.').max(200, 'Use 200 characters or fewer.'),
  description: z.string().max(200, 'Use 200 characters or fewer.'),
});

type CreateCustomProgramForm = z.infer<typeof createCustomProgramSchema>;

export const CreateCustomProgramModal: FC<CreateCustomProgramModalProps> = ({ orgId }) => {
  const { closeModal } = useModal();
  const [createFramework] = useInsertFrameworkMutation();

  const { refetch: refetchFrameworks } = useGetAvailableFrameworksForOrganizationQuery({
    orgId,
  });
  const [createProgram] = useCreateProgramMutation();
  const { refetch } = useGetProgramsQuery({ orgId });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
  } = useForm<CreateCustomProgramForm>({
    resolver: zodResolver(createCustomProgramSchema),
    defaultValues: {
      programName: '',
      description: '',
    },
    shouldFocusError: false,
  });

  const onClose = useStableCallback(() => {
    closeModal();
    reset();
  });

  const onSubmit: SubmitHandler<CreateCustomProgramForm> = async (data) => {
    try {
      const { insert_frameworks_one: newFramework } = await createFramework({
        input_payload: {
          name: data.programName,
          organization_id: orgId,
          active: true,
        },
      }).unwrap();

      refetchFrameworks();

      await createProgram({
        programInput: {
          name: data.programName,
          description: data.description,
          organization_id: orgId,
          frameworkType: FrameworkType.Custom,
          framework_id: newFramework?.id as string,
        },
      }).unwrap();
      successToast('Program successfully created!');
    } catch {
      errorToast('Unable to create program. Try again later.');
    }
    refetch();
    onClose();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <ModalBody>
        <Stack spacing={6}>
          <Controller
            control={control}
            name="programName"
            render={({ field, fieldState }) => (
              <TextField
                ref={field.ref}
                label="Name"
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="Enter program name"
                error={fieldState.error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="description"
            render={({ field, fieldState }) => (
              <TextField
                ref={field.ref}
                label="Description"
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder="Enter program description"
                error={fieldState.error?.message}
              />
            )}
          />
        </Stack>
      </ModalBody>
      <ModalFooter gap={2}>
        <Button variant="soft" onPress={() => onClose()}>
          Cancel
        </Button>
        <Button isPending={isSubmitting} type="submit">
          Submit
        </Button>
      </ModalFooter>
    </form>
  );
};
